import React, { useState, useEffect } from "react";
import { FaPlus, FaTrash, FaBuilding, FaFileAlt } from "react-icons/fa";
import AdminModal from "../../components/AdminModal";
import ScreenHeader from "../../components/ScreenHeader";
import LoadingState from "../../components/LoadingState";
import { colors, shadows, radius, spacing, typography, transitions, cardBase, btnPrimary, btnSecondary, btnDanger, inputBase } from "../../utils/designTokens";
import { useBuilding } from "../../context/BuildingContext";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

const CriteriosPorEdificios = ({ setSelected, embedded = false }) => {
  const { buildings, selectedBuilding, setSelectedBuilding } = useBuilding();
  
  // Usar el filtro global del contexto
  const buildingFilter = selectedBuilding?.buildingid || (buildings && buildings.length === 1 ? buildings[0].buildingid : null);
  
  const [criteriaBuilding, setCriteriaBuilding] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableCriteria, setAvailableCriteria] = useState([]);
  const [form, setForm] = useState({ field: "", name: "" });
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  // Cargar criterios ya agregados para el edificio
  const fetchCriteriaBuilding = async () => {
    if (!buildingFilter) {
      setCriteriaBuilding([]);
      return;
    }

    setLoading(true);
    try {
      const q = query(
        collection(db, "criteriabuilding"),
        where("buildingid", "==", Number(buildingFilter))
      );
      const snap = await getDocs(q);
      const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setCriteriaBuilding(data);
    } catch (error) {
      console.error("Error cargando criteriabuilding:", error);
      setCriteriaBuilding([]);
    } finally {
      setLoading(false);
    }
  };

  // Cargar criterios disponibles para el desplegable
  const fetchAvailableCriteria = async () => {
    if (!buildingFilter) {
      setAvailableCriteria([]);
      return;
    }

    try {
      // 1. Criterios generales (siempre disponibles)
      const generalCriteria = [
        { field: "all", name: "Todos", criteria: "All" },
        { field: "tenant", name: "Inquilinos", criteria: "Inquilino" },
        { field: "owner", name: "Propietarios", criteria: "Propietarios" },
      ];

      // 2. Obtener todos los criterios de criteriaexpenses
      const criteriaQuery = query(collection(db, "criteriaexpenses"));
      const criteriaSnap = await getDocs(criteriaQuery);
      const criteriaList = criteriaSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // 3. Filtrar criterios específicos: mostrar TODOS los criterios de criteriaexpenses
      // (excepto los que ya están en generalCriteria)
      const specificCriteria = [];
      for (const criteria of criteriaList) {
        const field = criteria.field?.toLowerCase();
        if (!field || field === "all" || field === "tenant" || field === "owner") {
          continue; // Ya están en generalCriteria
        }

        // Agregar todos los criterios disponibles, sin importar si algún apartment tiene el campo
        specificCriteria.push({
          field: field,
          name: criteria.name || criteria.criteria || field,
          criteria: criteria.criteria || field,
        });
      }

      // 5. Combinar y filtrar los que ya están agregados
      const allAvailable = [...generalCriteria, ...specificCriteria];
      const alreadyAdded = new Set(criteriaBuilding.map((f) => f.field));
      const filtered = allAvailable.filter((c) => !alreadyAdded.has(c.field));

      setAvailableCriteria(filtered);
    } catch (error) {
      console.error("Error cargando criterios disponibles:", error);
      setAvailableCriteria([]);
    }
  };

  useEffect(() => {
    fetchCriteriaBuilding();
  }, [selectedBuilding, buildings]);

  useEffect(() => {
    if (buildingFilter) {
      fetchAvailableCriteria();
    }
  }, [selectedBuilding, criteriaBuilding]);

  const handleAddCriteria = async () => {
    if (!form.field || !form.name.trim()) {
      setNotification({ type: "error", message: "Completa el campo y el nombre del criterio" });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    setSaving(true);
    try {
      await addDoc(collection(db, "criteriabuilding"), {
        field: form.field,
        buildingid: Number(buildingFilter),
        name: form.name.trim(),
        createdAt: serverTimestamp(),
      });

      setNotification({ type: "success", message: "Criterio agregado exitosamente" });
      setTimeout(() => setNotification(null), 4000);
      setForm({ field: "", name: "" });
      setShowAddModal(false);
      fetchCriteriaBuilding();
    } catch (error) {
      console.error("Error agregando criterio:", error);
      setNotification({ type: "error", message: "Error al agregar el criterio: " + error.message });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCriteria = (id) => {
    setConfirmModal({
      message: "¿Estas seguro de eliminar este criterio?",
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await deleteDoc(doc(db, "criteriabuilding", id));
          setNotification({ type: "success", message: "Criterio eliminado exitosamente" });
          setTimeout(() => setNotification(null), 4000);
          fetchCriteriaBuilding();
        } catch (error) {
          console.error("Error eliminando criterio:", error);
          setNotification({ type: "error", message: "Error al eliminar el criterio: " + error.message });
          setTimeout(() => setNotification(null), 4000);
        }
      },
    });
  };

  // Si no hay edificio seleccionado
  if (!buildingFilter) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyStateMain}>
          <div style={styles.emptyStateIcon}>
            <FaBuilding size={48} color={colors.primary} />
          </div>
          <h2 style={styles.emptyTitle}>Selecciona un Edificio</h2>
          <p style={styles.emptyMessage}>
            Para gestionar los criterios, utiliza el filtro en la barra superior.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={embedded ? {} : styles.container}>
      {notification && (
        <div style={{
          padding: "12px 16px",
          marginBottom: spacing[4],
          borderRadius: radius.sm,
          backgroundColor: notification.type === "error" ? colors.errorLight : colors.successLight,
          color: notification.type === "error" ? colors.errorDark : colors.success,
          boxShadow: shadows.xs,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: typography.sizes.md,
        }}>
          <span>{notification.message}</span>
          <button onClick={() => setNotification(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "inherit" }} aria-label="Cerrar notificación">×</button>
        </div>
      )}
      {/* Header con título y acciones */}
      <div style={embedded ? { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: spacing[4] } : styles.pageHeader}>
        {!embedded && <ScreenHeader icon={FaFileAlt} title="Criterios" subtitle="Criterios de división de gastos" />}
        {embedded && <h4 style={{ fontSize: typography.sizes.lg, fontWeight: 400, color: colors.black, margin: 0 }}>Criterios de División de Gastos</h4>}
        <button
          style={{
            ...styles.addBtn,
            opacity: availableCriteria.length === 0 ? 0.5 : 1,
            cursor: availableCriteria.length === 0 ? "not-allowed" : "pointer",
          }}
          onClick={() => {
            if (availableCriteria.length > 0) {
              setShowAddModal(true);
            } else {
              setNotification({ type: "error", message: "No hay criterios disponibles para agregar." });
              setTimeout(() => setNotification(null), 4000);
            }
          }}
        >
          <FaPlus size={12} /> Agregar
        </button>
      </div>

      {/* Contenido cuando hay edificio seleccionado */}
      <div style={styles.content}>

        {loading ? (
          <LoadingState message="Cargando criterios…" />
        ) : criteriaBuilding.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>No hay criterios agregados para este edificio.</p>
            <p style={styles.emptySubtext}>Utiliza el botón "Agregar" para comenzar.</p>
          </div>
        ) : (
          <div style={styles.table}>
            <div style={styles.thead}>
              <div>Nombre</div>
              <div>Campo</div>
              <div>Acciones</div>
            </div>
            {criteriaBuilding.map((item) => (
              <div key={item.id} style={styles.trow}>
                <div>{item.name}</div>
                <div style={styles.fieldBadge}>{item.field}</div>
                <div>
                  <button
                    style={styles.deleteBtn}
                    onClick={() => handleDeleteCriteria(item.id)}
                    title="Eliminar criterio"
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Agregar Criterio */}
      <AdminModal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setForm({ field: "", name: "" });
        }}
        title="Agregar Criterio"
        size="narrow"
        footer={
          <>
            <button
              style={styles.btnCancel}
              onClick={() => {
                setShowAddModal(false);
                setForm({ field: "", name: "" });
              }}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              style={{ ...styles.btnSave, opacity: saving ? 0.7 : 1 }}
              onClick={handleAddCriteria}
              disabled={saving || !form.field || !form.name.trim()}
            >
              {saving ? "Guardando..." : "Agregar"}
            </button>
          </>
        }
      >
        <div style={styles.formGroup}>
          <label style={styles.label}>Criterio *</label>
          <select
            style={styles.input}
            value={form.field}
            onChange={(e) => {
              const selected = availableCriteria.find((c) => c.field === e.target.value);
              setForm({
                field: e.target.value,
                name: selected?.name || "",
              });
            }}
          >
            <option value="">Seleccionar criterio...</option>
            {availableCriteria.map((criteria) => (
              <option key={criteria.field} value={criteria.field}>
                {criteria.name}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Nombre *</label>
          <input
            type="text"
            style={styles.input}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ej: Todos"
          />
          <p style={styles.helpText}>
            Este nombre se mostrará al seleccionar el criterio en los gastos.
          </p>
        </div>
      </AdminModal>

      {/* Modal Confirmar Eliminación */}
      <AdminModal
        open={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        title="Confirmar acción"
        size="narrow"
        footer={
          <>
            <button onClick={() => setConfirmModal(null)} style={{ ...btnSecondary, padding: "8px 16px" }}>Cancelar</button>
            <button onClick={confirmModal?.onConfirm} style={{ ...btnDanger, padding: "8px 16px" }}>Confirmar</button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: typography.sizes.md, color: colors.gray600 }}>{confirmModal?.message}</p>
      </AdminModal>
    </div>
  );
};

// Estilos con design tokens
const styles = {
  container: {
    padding: spacing[6],
    backgroundColor: colors.gray50,
    minHeight: "100vh",
    fontFamily: typography.fontFamily,
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing[3],
  },
  headerTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  pageTitle: {
    fontFamily: typography.fontFamily,
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.primary,
    margin: 0,
    letterSpacing: "-0.01em",
  },
  pageSubtitle: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: 0,
  },
  // Contenido principal — sin borde, solo sombra
  content: {
    ...cardBase,
  },
  addBtn: {
    ...btnPrimary,
    fontFamily: typography.fontFamily,
  },
  loading: {
    padding: 40,
    textAlign: "center",
    color: colors.gray600,
  },
  emptyState: {
    padding: 40,
    textAlign: "center",
  },
  // Estado vacío principal (sin edificio seleccionado)
  emptyStateMain: {
    ...cardBase,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "64px 32px",
    textAlign: "center",
    marginTop: spacing[6],
  },
  emptyStateIcon: {
    marginBottom: spacing[5],
    opacity: 0.8,
  },
  emptyText: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: "0 0 8px 0",
  },
  emptySubtext: {
    fontSize: typography.sizes.base,
    color: colors.gray400,
    margin: 0,
  },
  // Tabla de criterios — sin borde, solo sombra
  table: {
    ...cardBase,
    padding: 0,
    overflow: "hidden",
  },
  thead: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 80px",
    gap: spacing[4],
    padding: "14px 20px",
    background: colors.gray50,
    color: colors.gray600,
    fontWeight: 400,
    fontSize: typography.sizes.sm,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  trow: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 80px",
    gap: spacing[4],
    padding: "14px 20px",
    borderTop: `1px solid ${colors.gray100}`,
    fontSize: typography.sizes.base,
    alignItems: "center",
  },
  fieldBadge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: radius.sm,
    background: colors.primaryLighter,
    color: colors.primary,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
  },
  deleteBtn: {
    background: "transparent",
    border: `1px solid ${colors.errorLight}`,
    borderRadius: radius.sm,
    padding: "6px 10px",
    cursor: "pointer",
    color: colors.errorDark,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: `all ${transitions.normal}`,
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray900,
    fontFamily: typography.fontFamily,
  },
  input: {
    ...inputBase,
  },
  helpText: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    margin: 0,
  },
  btnCancel: {
    ...btnSecondary,
    fontFamily: typography.fontFamily,
  },
  btnSave: {
    ...btnPrimary,
    fontFamily: typography.fontFamily,
  },
  backBtnAlone: {
    padding: 10,
    background: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    color: colors.black,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: `all ${transitions.normal}`,
  },
  emptyTitle: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.black,
    margin: "0 0 8px 0",
    fontFamily: typography.fontFamily,
  },
  emptyMessage: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: 0,
    lineHeight: 1.6,
    maxWidth: 320,
  },
};

export default CriteriosPorEdificios;
